// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * Developer telemetry for the shot-tracking pipeline.
 *
 * The capture session (services/shotTracking) and the round hooks report every
 * step here — fixes, stationarity, microphone windows, metering, impacts,
 * marks, server fusion results — and the developer debug page renders them
 * live. This exists for field calibration: the detection thresholds were set
 * from theory, and tuning them requires seeing what the sensors actually said
 * the moment a real swing was missed or a rustle fired.
 *
 * Costs nothing in normal play: emit() is a no-op object build behind a
 * listener check, and the chatty metering stream is only forwarded live, never
 * buffered.
 */

export type ShotTrackingDebugEvent =
  | { kind: 'session'; at: number; message: string }
  | {
      kind: 'fix';
      at: number;
      lat: number;
      lon: number;
      accuracy: number | null;
      speed: number | null;
      stationary: boolean;
      listening: boolean;
    }
  | { kind: 'mic'; at: number; open: boolean }
  | { kind: 'metering'; at: number; db: number; baseline?: number; rise?: number }
  | { kind: 'impact'; at: number; riseDb: number }
  | {
      /**
       * A sound that rose clearly above baseline but stayed under the trigger
       * threshold. Putts and chips live here — logging them is what turns a
       * green session into calibration data instead of silence.
       */
      kind: 'nearMiss';
      at: number;
      riseDb: number;
    }
  | {
      kind: 'mark';
      at: number;
      markId: string;
      source: string;
      /** Stroke or putt; absent on events from before the classes split. */
      markKind?: 'stroke' | 'putt';
      lat: number | null;
      lon: number | null;
      accuracyMeters: number | null;
      holeNumber: number | null;
      /**
       * How many GPS fixes were averaged into the attached position. Calibration
       * needs it to tell a mark backed by a five-fix standing cluster from one
       * backed by a single fix snatched as the player kept walking — the two
       * carry the same accuracyMeters shape but not the same trust.
       */
      sampleCount?: number;
    }
  | { kind: 'send'; at: number; markId: string; outcome: 'sent' | 'queued' | 'skipped' }
  | {
      /** A wrist-acceleration peak relayed from the watch's putt-probe mode. */
      kind: 'wearPeak';
      at: number;
      magnitudeMps2: number;
      /** Wrist stillness before the burst, ms — the putt discriminator. */
      stillnessMs?: number;
      /** Peak wrist rotation during the burst, rad/s — the swing discriminator. */
      rotationRadS?: number;
    }
  | {
      kind: 'server';
      at: number;
      hole: number;
      /** Detected putts on the hole, fused server-side. */
      putts?: number;
      shots: Array<{
        shotNumber: number;
        distanceMeters: number | null;
        confidence: string;
        sources?: string[];
      }>;
    };

type Listener = (event: ShotTrackingDebugEvent) => void;

/** Everything except metering, so a page opened mid-round still has history. */
const BUFFER_LIMIT = 300;

const listeners = new Set<Listener>();
let buffer: ShotTrackingDebugEvent[] = [];

export const hasShotTrackingDebugListeners = (): boolean => listeners.size > 0;

export const emitShotTrackingDebug = (event: ShotTrackingDebugEvent): void => {
  if (event.kind !== 'metering') {
    buffer.push(event);
    if (buffer.length > BUFFER_LIMIT) {
      buffer = buffer.slice(-BUFFER_LIMIT);
    }
  }
  for (const listener of listeners) {
    try {
      listener(event);
    } catch {
      // A broken debug page must never take the capture session down.
    }
  }
};

/** Recent history, oldest first. Metering is live-only and never appears here. */
export const getShotTrackingDebugHistory = (): ShotTrackingDebugEvent[] => buffer.slice();

export const subscribeShotTrackingDebug = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const __resetShotTrackingDebugForTests = (): void => {
  listeners.clear();
  buffer = [];
};
